import 'library/styles/index.css';
import '@src/styles/tokens.css';
import '@src/styles/design-tokens.css';
import '@src/styles/fonts.css';
import '@src/styles/globals.css';
import '@src/styles/reset.css';
import { OverlayProvider } from '@react-aria/overlays';
import {
  QueryClient as TanStackQueryClient,
  QueryClientProvider as TanStackQueryClientProvider,
} from '@tanstack/react-query';
import { SpeedInsights } from '@vercel/speed-insights/next';
import { NextIntlClientProvider } from 'next-intl';
import type { AppProps as DefaultAppProps } from 'next/app';
import Head from 'next/head';
import { NextRouter, withRouter } from 'next/router';
import { NuqsAdapter } from 'nuqs/adapters/next/pages';
import { useMemo, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { QueryClient, QueryClientProvider } from 'react-query';

import { UserConsentProvider } from '@numa-group/features.consent-management';

import { BrandTheme } from '@src/components/common/brand-theme';
import { ErrorBoundary } from '@src/components/common/error-boundary/error-boundary';
import { LayoutComponent } from '@src/components/layouts/layout-component';
import { GenericError } from '@src/components/organisms/generic-error';
import { SEGMENT_ID_APP, SEGMENT_ID_KIOSK, SEGMENT_ID_WEB } from '@src/constants/analytics';
import { BrandProvider } from '@src/contexts/brand-context';
import { CmsContentProvider } from '@src/contexts/cms-content-context';
import { FeaturesProvider } from '@src/contexts/features-context';
import { ToastProvider } from '@src/contexts/toast-context';
import { UserAgentProvider } from '@src/contexts/user-agent-context/user-agent-context';
import { WindowProvider } from '@src/contexts/window-context';
import { getFeatureList } from '@src/features';
import { AnalyticsProvider } from '@src/features/analytics/contexts';
import { TripDetailsPage } from '@src/generated/dato-cms';
import { useDeepLinkBackNavigation } from '@src/hooks/use-deep-link-back-navigation';
import { DEFAULT_TIME_ZONE, Locale, i18nConfig } from '@src/i18n/locales';
import { BrandData, CommonData, TripsData } from '@src/lib/ssr/types';
import { AppDialogCoordinator } from '@src/utils/dialog-coordination';
import { initDash0 } from '@src/utils/observability/init-dash0';
import { reportCmpTimeout } from '@src/utils/observability/report-error';
import { useDash0UserIdentity } from '@src/utils/observability/use-dash0-identity';
import { selectSegmentWriteKey } from '@src/utils/select-segment-write-key';

// Module scope so the SDK starts as soon as the client bundle evaluates.
// Next 14 has no instrumentation-client.ts convention; initDash0 guards
// against SSR and double init (the app router inits via <Dash0Init />).
initDash0();

type Props = TripsData &
  BrandData &
  CommonData & {
    property: TripDetailsPage['property'];
    unitGroup: TripDetailsPage['unitGroup'];
  };

type AppProps = DefaultAppProps<Props> & {
  Component: DefaultAppProps['Component'] & {
    Layout?: LayoutComponent<Props>;
  };
} & {
  router: NextRouter;
};

const App = ({ Component, pageProps, router }: AppProps) => {
  const [queryClient] = useState(() => new QueryClient());
  // New queries are on TanStack Query v5; the legacy ones stay on react-query v3 until they move.
  const [tanStackQueryClient] = useState(() => new TanStackQueryClient());

  // Global owner of browser-side Dash0 profile identity: covers every page
  // whose SSR provided a profile cache, regardless of layout. ProfileProvider
  // adds the query-driven stamp for first login, before the cache exists.
  useDash0UserIdentity(pageProps.profileCache);
  useDeepLinkBackNavigation();

  const ComponentWithLayout = useMemo(() => {
    return Component.Layout?.(Component) ?? Component;
  }, [Component]);

  const features = getFeatureList({
    isWhiteLabel: pageProps.isWhiteLabel,

    areMemberBenefitsEnabled:
      !!pageProps.cmsContent?.featureToggle?.memberBenefitsProduct && !!pageProps.currentTrip?.hasMemberBenefits,
    npsSurveyCopyAbExperiment: !!pageProps.cmsContent?.featureToggle?.tmNpsSurveyCopyAbExperiment,

    upgradeUpsellEnabled: 'property' in pageProps && !!pageProps.property?.enableRoomUpgrade,
  });

  const segmentId = selectSegmentWriteKey({
    isKiosk: pageProps.isKiosk,
    isAppWebView: pageProps.isAppWebView,
    segmentIdKiosk: SEGMENT_ID_KIOSK,
    segmentIdApp: SEGMENT_ID_APP,
    segmentIdWeb: SEGMENT_ID_WEB,
  });
  const locale = (router.query.locale as Locale) || i18nConfig.defaultLocale;

  const viewportSettings = pageProps.isAppWebView
    ? 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
    : 'width=device-width, initial-scale=1.0';

  return (
    <>
      <NextIntlClientProvider locale={locale} timeZone={DEFAULT_TIME_ZONE}>
        <IntlProvider
          locale={locale}
          onError={(err) => {
            if (err.code === 'MISSING_TRANSLATION') {
              return;
            }
            throw err;
          }}
        >
          <QueryClientProvider client={queryClient}>
            <TanStackQueryClientProvider client={tanStackQueryClient}>
              <NuqsAdapter>
                <AppDialogCoordinator>
                  <UserConsentProvider onCmpTimeout={reportCmpTimeout}>
                    <UserAgentProvider value={{ isAppWebView: pageProps.isAppWebView }}>
                      <AnalyticsProvider
                        segmentId={segmentId}
                        brand={pageProps.brand}
                        currentTrip={pageProps.currentTrip}
                        profile={pageProps.profileCache}
                        property={pageProps.property}
                        unitGroup={pageProps.unitGroup}
                      >
                        <BrandProvider
                          value={{
                            brand: pageProps.brand,
                            isWhiteLabel: pageProps.isWhiteLabel,
                            isNativeBrand: pageProps.isNativeBrand,
                          }}
                        >
                          {pageProps.isWhiteLabel && <BrandTheme brand={pageProps.brand} />}
                          <Head>
                            <meta name="viewport" content={viewportSettings} />
                          </Head>

                          <WindowProvider>
                            <OverlayProvider>
                              <ToastProvider>
                                <ErrorBoundary attributes={{ location: 'client-side' }} fallback={<GenericError />}>
                                  <CmsContentProvider value={pageProps.cmsContent}>
                                    <FeaturesProvider value={features}>
                                      <ComponentWithLayout {...pageProps} />
                                    </FeaturesProvider>
                                  </CmsContentProvider>
                                </ErrorBoundary>
                              </ToastProvider>
                            </OverlayProvider>
                          </WindowProvider>
                        </BrandProvider>
                      </AnalyticsProvider>
                    </UserAgentProvider>
                  </UserConsentProvider>
                </AppDialogCoordinator>
              </NuqsAdapter>
            </TanStackQueryClientProvider>
          </QueryClientProvider>
        </IntlProvider>
      </NextIntlClientProvider>

      <SpeedInsights sampleRate={0.1} />
    </>
  );
};
export default withRouter(App);
